//@flow

import * as React from "react"
import {
  TweenMax,
  Cubic,
  Back
} from "gsap"
import SVGLoader from "../../../UI/SVGLoader"

import {
  Notch,
  Thumb,
  Track,
  SliderContainer,
  Segment,
  Label,
  ThumbContent
} from "./UI"
import iconThumb from "../../Icons/iconThumb.svg"

import type {
  CoordinateConversion
} from "../../FixedResolutionContainer"

export type PageCoord = {
  pageX: number,
  pageY: number
}

export type State = {
  containerWidth: number,
  isPressed: boolean,
  percent: number
}

type Props = {
  delay: number,
  scale: number,
  conversion: CoordinateConversion,
  onChange ? : (percent: number) => any
}

class Slider extends React.Component < Props, State > {
  track: ? HTMLElement
  thumb: ? HTMLElement
  container: ? HTMLElement
  labels: HTMLElement[]
  notches: HTMLElement[]
  segments: HTMLElement[]

  constructor(props: Props) {
    super(props)
    this.labels = []
    this.notches = []
    this.container = null
    this.thumb = null
    this.track = null
    this.segments = []
    this.state = {
      isPressed: false,
      percent: 0,
      containerWidth: 0
    }
  }

  componentDidMount() {
    const {
      delay
    } = this.props
    const labels = this.labels.filter(el => el !== null)
    const notches = this.notches.filter(el => el !== null)

    TweenMax.set(this.container, {
      y: "+=100",
      opacity: 0
    })
    TweenMax.to(this.container, 0.5, {
      opacity: 1,
      ease: Cubic.easeOut,
      delay
    })
    TweenMax.to(this.container, 0.5, {
      y: 0,
      ease: Back.easeOut,
      delay
    })

    TweenMax.set(this.track, {
      scaleX: "0",
      opacity: 0
    })
    TweenMax.to(this.track, 0.25, {
      opacity: 1,
      ease: Cubic.easeOut,
      delay: delay + 0.25
    })
    TweenMax.to(this.track, 0.5, {
      transformOrigin: "0% 0%",
      scaleX: 1,
      ease: Cubic.easeOut,
      delay: delay + 0.25
    })

    TweenMax.set(notches, {
      scaleY: 0
    })
    TweenMax.staggerTo(
      notches,
      0.25, {
        scaleY: 1,
        transformOrigin: "0% 0%",
        delay: delay + 0.5
      },
      0.1
    )

    TweenMax.set(labels, {
      opacity: 0,
      x: "+=10"
    })
    TweenMax.staggerTo(
      labels,
      0.25, {
        x: 0,
        opacity: 1,
        delay: delay + 0.5
      },
      0.1
    )

    TweenMax.set(this.thumb, {
      scaleX: 0,
      scaleY: 0,
      opacity: 0
    })
    TweenMax.to(this.thumb, 0.25, {
      opacity: 1,
      ease: Cubic.easeOut,
      delay: delay + 0.75
    })
    TweenMax.to(this.thumb, 0.25, {
      scaleX: 1,
      scaleY: 1,
      ease: Back.easeOut,
      delay: delay + 0.75
    })

    this.startListeningForTouches()

    this.setState(() => ({
      containerWidth: this.track ? this.track.offsetWidth : 0
    }))
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      isPressed,
      percent
    } = this.state
    if (isPressed !== prevState.isPressed) {
      this.indicatePressedState(isPressed)
    }
    if (percent !== prevState.percent) {
      this.animateToPercent(percent)
    }
  }

  indicatePressedState = (isPressed: boolean) => {
    TweenMax.to(this.thumb, 0.25, {
      scale: isPressed ? 1.5 : 1,
      ease: Back.easeOut
    })
  }

  animateToPercent = (percent: number) => {
    if (this.track) {
      const width = this.track.offsetWidth
      TweenMax.to(this.thumb, 0.1, {
        x: `${percent * width}px`,
        ease: Cubic.easeOut
      })
    }
  }

  startListeningForTouches = () => {
    window.addEventListener("touchstart", this.handleTouchStart)
    window.addEventListener("touchmove", this.handleTouchMove)
    window.addEventListener("touchend", this.handleMouseUp)
    window.addEventListener("mousedown", this.handleMouseDown)
    window.addEventListener("mousemove", this.handleMouseMove)
    window.addEventListener("mouseup", this.handleMouseUp)
  }

  stopListeningForTouches = () => {
    window.removeEventListener("touchstart", this.handleTouchStart)
    window.removeEventListener("touchmove", this.handleTouchMove)
    window.removeEventListener("touchend", this.handleMouseUp)
    window.removeEventListener("mousedown", this.handleMouseDown)
    window.removeEventListener("mousemove", this.handleMouseMove)
    window.removeEventListener("mouseup", this.handleMouseUp)
  }

  handleTouchStart = (e: TouchEvent) => {
    this.handleMouseDown(e)
  }

  handleMouseDown = (e: MouseEvent | TouchEvent) => {
    if (e.target instanceof HTMLElement) {
      if (this.container && this.container.contains(e.target)) {
        e.preventDefault()
        e.stopPropagation()
        this.setState(() => ({
          isPressed: true
        }))
        if (e instanceof MouseEvent) {
          const {
            pageX,
            pageY
          } = e
          this.moveTo(pageX, pageY)
        } else if (e instanceof TouchEvent) {
          const {
            pageX,
            pageY
          } = e.touches[0]
          this.moveTo(pageX, pageY)
        }
      }
    }
  }

  handleTouchMove = (e: TouchEvent) => {
    const {
      isPressed
    } = this.state
    if (isPressed) {
      e.stopPropagation()
      e.preventDefault()
    }
    const {
      pageX,
      pageY
    } = e.touches[0]
    this.moveTo(pageX, pageY)
  }

  handleMouseMove = (e: MouseEvent) => {
    const {
      isPressed
    } = this.state
    if (isPressed) {
      e.stopPropagation()
      e.preventDefault()
    }
    const {
      pageX,
      pageY
    } = e
    this.moveTo(pageX, pageY)
  }

  moveTo = (pageX: number, pageY: number) => {
    const {
      onChange,
      conversion
    } = this.props
    const {
      isPressed
    } = this.state
    if (this.track && isPressed) {
      const x = this.track.offsetLeft
      const mouseX = conversion({
        x: pageX,
        y: 0
      }).x * this.props.scale
      const width = this.track ? this.track.offsetWidth * this.props.scale : 0 // Get the actual coord w/ offset then reapply scale
      const percent = Math.max(0, Math.min(1, (mouseX - x) / width))
      this.setState(() => ({
        percent
      }))
      onChange && onChange(percent)
    }
  }

  handleMouseUp = (e: Event) => {
    this.setState(() => ({
      isPressed: false
    }))
  }

  componentWillUnmount() {
    this.stopListeningForTouches()
  }

  render() {
    return ( <
      SliderContainer innerRef = {
        el => (this.container = el)
      } >
      <
      Thumb innerRef = {
        el => (this.thumb = el)
      } >
      <
      ThumbContent >
      <
      SVGLoader path = {
        iconThumb
      }
      height = "100%"
      width = "100%"
      fill = "#fff" /
      >
      <
      /ThumbContent> <
      /Thumb> <
      Track innerRef = {
        el => (this.track = el)
      } >
      <
      Segment innerRef = {
        el => this.segments.push(el)
      } >
      <
      Notch innerRef = {
        el => this.notches.push(el)
      }
      /> <
      Label innerRef = {
        el => this.labels.push(el)
      } > 7 AM < /Label> <
      /Segment> <
      Segment innerRef = {
        el => this.segments.push(el)
      } >
      <
      Notch innerRef = {
        el => this.notches.push(el)
      }
      /> <
      Label innerRef = {
        el => this.labels.push(el)
      } > 8 AM < /Label> <
      /Segment> <
      Segment innerRef = {
        el => this.segments.push(el)
      } >
      <
      Notch innerRef = {
        el => this.notches.push(el)
      }
      /> <
      Label innerRef = {
        el => this.labels.push(el)
      } > 9 AM < /Label> <
      /Segment> <
      Segment innerRef = {
        el => this.segments.push(el)
      } >
      <
      Notch innerRef = {
        el => this.notches.push(el)
      }
      /> <
      Label innerRef = {
        el => this.labels.push(el)
      } > 10 AM < /Label> <
      /Segment> <
      Segment innerRef = {
        el => this.segments.push(el)
      } >
      <
      Notch innerRef = {
        el => this.notches.push(el)
      }
      /> <
      Label innerRef = {
        el => this.labels.push(el)
      } > 11 AM < /Label> <
      /Segment> <
      Segment innerRef = {
        el => this.segments.push(el)
      } >
      <
      Notch innerRef = {
        el => this.notches.push(el)
      }
      /> <
      Label innerRef = {
        el => this.labels.push(el)
      } > 12 PM < /Label> <
      /Segment> <
      /Track> <
      /SliderContainer>
    )
  }
}

export default Slider


// WEBPACK FOOTER //
// ./src/Kiosk/WoodburningView/Slider/index.js